<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA
    import { onMount, onDestroy } from 'svelte';
    import { __, remToPx, getActualKey } from '@oat-sa-private/ui-core';
    import { Button, ScrollableWrapper } from '@oat-sa-private/ui-elements';
    import { OverlayBox } from '@oat-sa-private/ui-components';
    import router from '@/core/router/router';
    import { postAcs, postAuthorize, deleteDeliveryExecution } from '@/core/api/api';
    import { routes, actionsList } from '@/core/utils/consts';
    import { formatMomentDatetime } from '@/core/utils/date.js';
    import { rejectedAcs, buildAcsNotification } from '@/store/acs';
    import { retryingAcs } from '@/store/retryingAcs';
    import { getCategories, getSubcategories } from '@/component/report/dropdownOptions';
    import getInteractionUserName from '@/core/utils/getInteractionUserName';

    const categories = getCategories();
    const subcategories = getSubcategories();
    let recreateNotificationsOnDestroy = true;

    let causes;

    $: {
        const reasonCode = $retryingAcs[0]?.reason_code;
        const categoryKey = getCategoryKey(reasonCode);
        const causesArr = [categories[categoryKey]];
        // other reason code is 999
        if (categoryKey && reasonCode && reasonCode !== '999') {
            causesArr.push(subcategories[categoryKey][reasonCode]);
        }
        causes = causesArr.join(' - ');
    }

    $: reasons = $retryingAcs[0]?.reason_msg;

    $: times = formatTimestamp($retryingAcs[0]?.incident_time);

    $: failedAcs = $retryingAcs.length;

    $: userName = getInteractionUserName({
        name: $retryingAcs[0]?.userName || '',
        id: $retryingAcs[0]?.sub || ''
    });

    $: timeManual = $retryingAcs[0]?.incident_time_manual;

    $: action = $retryingAcs[0]?.action;

    $: displayInfo = times || reasons || causes;

    /**
     * Find category key by subcategory key
     * @param {String} subcategoryKey - subcategory key
     * @returns {String} - category key
     */
    function getCategoryKey(subcategoryKey) {
        return Object.keys(categories).find(key => Object.keys(subcategories[key]).includes(subcategoryKey));
    }

    /**
     * Format timestamp considering the locale
     * @param {String} ts - timestamp
     * @returns {String} - localized timestamp
     */
    function formatTimestamp(ts) {
        return formatMomentDatetime(ts);
    }

    /**
     * Remove retrying ACS from rejected ACS store
     */
    function removeRejectedAcs() {
        rejectedAcs.update(storeValue => storeValue.filter(item => !$retryingAcs.includes(item)));
    }

    /**
     * Clear retrying ACS store
     */
    function clearRetryingAcs() {
        retryingAcs.update(() => []);
    }

    /**
     * Retry reports
     * @param {SubmitEvent} evt - form submit event
     */
    function handleSubmit(evt) {
        evt.preventDefault();
        recreateNotificationsOnDestroy = false;
        removeRejectedAcs();
        if (action === actionsList.authorize) {
            postAuthorize($retryingAcs);
        } else if (action === actionsList.reset) {
            deleteDeliveryExecution($retryingAcs);
        } else {
            postAcs($retryingAcs);
        }
        router.redirect(routes.home);
    }

    /**
     * Handle OverlayBox event
     * @param {CustomEvent} e
     */
    function handleOverlayBoxAction(e) {
        if (e.detail.key === 'close') {
            closeOverlayBox();
        }
    }

    /**
     * Cancel reports retrying and redirect to another screen
     */
    function closeOverlayBox() {
        recreateNotificationsOnDestroy = false;
        removeRejectedAcs();

        // On closing this route we may want to show / or /details routes
        // It's essentially a 'back', so use that
        router.goBack();
    }

    /**
     * Handle key presses
     * @param {KeyboardEvent} event
     */
    function handleKeydown(event) {
        const keyname = getActualKey(event);
        if (keyname === 'esc') {
            event.stopPropagation();
            closeOverlayBox();
        }
    }

    // Calculations to provide correct height to ScrollableWrapper
    let windowHeight = 0;
    const overlayBoxHeaderHeight = remToPx(5.5);
    const paddingsHeight = remToPx(3 + 3 + 0.75);
    const nonScrollingHeight = overlayBoxHeaderHeight + paddingsHeight;
    $: scrollableWrapperCssHeight = `${windowHeight - nonScrollingHeight}px`;

    let wrapperElement;
    onMount(() => {
        wrapperElement.querySelector('.overlay-box')?.focus();
    });

    onDestroy(() => {
        if (recreateNotificationsOnDestroy) {
            buildAcsNotification($retryingAcs, false);
        }
        clearRetryingAcs();
    });
</script>

<style>
    .wrapper {
        height: 100%;
        padding: 3rem 4rem;

        & :global(.overlay-box) {
            height: 100%;
        }
        & :global(.overlay-box-content) {
            padding: 0;
        }
        & :global(.scrollable-wrapper) {
            padding: 2rem;
        }
        & form {
            width: 50%;
            margin: 2rem 25% 6rem;
        }
        @media screen and (--mq-maxwidth-medium) {
            & form {
                width: 80%;
                margin: 2rem 10% 6rem;
            }
        }
        & .form-header {
            font-size: var(--fontsize-heading-l);
            font-weight: bold;
        }
        & .form-body {
            border-top: 0.625rem solid black;
            padding-top: var(--space-5x);

            & .error-block {
                padding: var(--space-2x);
                background-color: var(--color-alert);
                color: var(--color-text-inverted);
            }
            & .info-block {
                padding: var(--space-3x);
                background-color: var(--color-bg-info);
            }
            & .info-block > * {
                margin-top: 0;
                margin-bottom: 0;
            }
            & .info-block > * + * {
                margin-top: var(--space-threequarters);
            }
        }
        & .form-footer {
            margin-top: var(--space-2x);
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
        }
    }
</style>

<svelte:window bind:innerHeight={windowHeight} on:keydown={handleKeydown} />

<div class="wrapper" bind:this={wrapperElement}>
    <OverlayBox on:action={handleOverlayBoxAction}>
        <ScrollableWrapper height={scrollableWrapperCssHeight}>
            <form on:submit={handleSubmit}>
                <div class="form-header">
                    <p>
                        {#if action === actionsList.authorize}
                            {failedAcs > 1 ? __('Failed authorizations') : `${__('Failed authorization')}:`}
                        {:else if action === actionsList.report}
                            {failedAcs > 1 ? __('Failed reports') : `${__('Failed report')}:`}
                        {:else if action === actionsList.pause}
                            {failedAcs > 1 ? __('Failed pauses') : `${__('Failed pause')}:`}
                        {:else if action === actionsList.resume}
                            {failedAcs > 1 ? __('Failed resumes') : `${__('Failed resume')}:`}
                        {:else if action === actionsList.terminate}
                            {failedAcs > 1 ? __('Failed terminations') : `${__('Failed termination')}:`}
                        {:else if action === actionsList.reopen}
                            {failedAcs > 1 ? __('Failed reopenings') : `${__('Failed reopening')}:`}
                        {:else if action === 'extendTime'}
                            {`${__('Failed extend time')}:`}
                        {:else if action === actionsList.reset}
                            {failedAcs > 1 ? __('Failed reset sesions') : `${__('Failed reset session')}:`}
                        {/if}
                        {failedAcs > 1 ? `(${failedAcs})` : `${userName}`}
                    </p>
                </div>
                <div class="form-body">
                    <div class="error-block">
                        {#if action === actionsList.authorize}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the authorization process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the authorization process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.report}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the report process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the report process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.pause}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the pause process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the pause process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.resume}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the resume process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the resume process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.terminate}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the termination process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the termination process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.reopen}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the reopening process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the reopening process for"
                                  )} ${userName}`}
                        {:else if action === 'extendTime'}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the extend time process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the extend time process for"
                                  )} ${userName}`}
                        {:else if action === actionsList.reset}
                            {failedAcs > 1
                                ? __(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the reset process for these test takers"
                                  )
                                : `${__(
                                      "Something went wrong and the task couldn't be performed. Please retry or cancel the reset process for"
                                  )} ${userName}`}
                        {/if}
                    </div>
                    {#if displayInfo}
                        <div class="info-block">
                            {#if causes}
                                <div><b>{__('Cause')}:</b> <span>{causes}</span></div>
                            {/if}
                            {#if reasons}
                                <div><b>{__('Commentary')} / {__('Reason')}:</b> <span>{reasons}</span></div>
                            {/if}
                            {#if times}
                                <div><b>{timeManual ? __('Time (manual)') : __('Time')}:</b> <span>{times}</span></div>
                            {/if}
                        </div>
                    {/if}
                </div>
                <div class="form-footer">
                    <Button
                        label={__('Abort')}
                        ariaLabel={__('Abort')}
                        skin="secondary"
                        shape="pill"
                        on:click={closeOverlayBox} />
                    <Button label={__('Retry')} ariaLabel={__('Retry')} skin="primary" shape="pill" type="submit" />
                </div>
            </form>
        </ScrollableWrapper>
    </OverlayBox>
</div>
